import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import Screen from '@/karehero-app/explore/Screen';
import Chat, { ChatInput } from '@/karehero-app/explore/Chat';
import { AA } from '@/karehero-app/explore/tell-us-once/TellUsOnce.content';

// Section C — Attendance Allowance end to end, conversation-first. Taken
// furthest because it's the most likely first candidate to try whole, and
// because C1 is the moment the entire idea stands or falls on.

// C1 — the translation moment, shown beside the form it has to satisfy. Not a
// phone frame: the whole argument is the pairing, and a mini screen on its own
// would hide the half that keeps us honest about what AA actually asks for.
export const C1Translation = () => {
  const c = AA.c1;
  return (
    <Stack sx={{ alignItems: 'stretch' }} direction='row' spacing={2}>
      <Stack sx={{ flex: 1, minWidth: 0 }} spacing={1}>
        <Typography variant='overline' color='text.secondary'>
          WHAT THEY SAY
        </Typography>
        <Box
          sx={{
            flex: 1,
            p: 1.5,
            bgcolor: 'background.default',
            border: 1,
            borderColor: 'custom.neutral.200',
            borderRadius: `${radius.md}px`,
          }}
        >
          <Chat turns={c.turns} />
        </Box>
      </Stack>

      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          width: 92,
          flexShrink: 0,
        }}
        spacing={1}
      >
        <Box sx={{ color: 'custom.coral.600', display: 'flex' }}>
          <Icon icon={'long-arrow-right' as never} fontSize={22} />
        </Box>
        <Typography
          sx={{ textAlign: 'center' }}
          variant='caption'
          color='text.secondary'
        >
          {c.translationNote}
        </Typography>
      </Stack>

      <Stack sx={{ flex: 1, minWidth: 0 }} spacing={1}>
        <Typography variant='overline' color='text.secondary'>
          WHAT THE FORM WANTS
        </Typography>
        <Paper variant='outlined' sx={{ flex: 1 }}>
          <Stack sx={{ p: 2 }} spacing={1.5}>
            <Stack spacing={0.25}>
              <Typography variant='subtitle2'>{c.formTitle}</Typography>
              <Typography variant='caption' color='text.secondary'>
                {c.formSection}
              </Typography>
            </Stack>

            <Divider />

            <Stack spacing={1.5}>
              {c.formFields.map((f) => (
                <Stack key={f.label} spacing={0.5}>
                  <Typography variant='caption' color='text.secondary'>
                    {f.label}
                  </Typography>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: '8px',
                      bgcolor: f.filled
                        ? 'custom.limeGreen.100'
                        : 'custom.neutral.50',
                      border: 1,
                      borderColor: f.filled
                        ? 'custom.limeGreen.200'
                        : 'custom.neutral.200',
                    }}
                  >
                    <Typography
                      sx={{ fontStyle: f.filled ? 'normal' : 'italic' }}
                      variant='body2'
                      color={f.filled ? 'text.primary' : 'text.secondary'}
                    >
                      {f.value || 'Still needed'}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  );
};
C1Translation.displayName = 'C1Translation';

// C2 — the moment the conversation needs something it can't talk its way to.
// The "to finish" tray at the bottom is the proposal being tested: deferred
// items need somewhere to live that review and drop-in/out can both read.
export const C2PhotoOrPrecise = () => {
  const c = AA.c2;
  return (
    <Screen footer={<ChatInput placeholder='Tell me anything…' />}>
      <Stack sx={{ pt: 1 }} spacing={2}>
        <Chat turns={c.turns} />

        <Stack spacing={1}>
          {c.options.map((o) => (
            <Paper key={o.label} variant='outlined'>
              <Stack
                sx={{ alignItems: 'center', p: 1.5 }}
                direction='row'
                spacing={1.5}
              >
                <Box sx={{ color: 'custom.coral.600', display: 'flex' }}>
                  <Icon icon={o.icon as never} fontSize={16} />
                </Box>
                <Typography sx={{ flex: 1, minWidth: 0 }} variant='body2'>
                  {o.label}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Stack>

        <Divider />

        <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
          <Box sx={{ color: 'custom.warmOrange.500', display: 'flex' }}>
            <Icon icon={'clock' as never} fontSize={13} />
          </Box>
          <Typography variant='caption' color='text.secondary'>
            {c.tray}
          </Typography>
        </Stack>
      </Stack>
    </Screen>
  );
};
C2PhotoOrPrecise.displayName = 'C2PhotoOrPrecise';

const SOURCE_META = {
  'you told me': { icon: 'message', color: 'custom.neutral.500' },
  'from a document': { icon: 'file-lines', color: 'custom.marineGreen.500' },
  'worked out from what you said': {
    icon: 'star',
    color: 'custom.cherry.500',
  },
} as const;

// C3 — confirm and edit, with provenance on every line. Default is yes;
// correcting is the exception. The inferred row is the one that has to earn
// its place, which is why its source tag is the loudest.
export const C3ConfirmEdit = () => {
  const c = AA.c3;
  return (
    <Screen
      footer={
        <Button ariaLabel={c.cta} isFullWidth>
          {c.cta}
        </Button>
      }
    >
      <Stack sx={{ pt: 2 }} spacing={2.5}>
        <Typography variant='h4'>{c.lead}</Typography>

        <Stack divider={<Divider />}>
          {c.answers.map((a) => {
            const meta = SOURCE_META[a.source];
            return (
              <Stack sx={{ py: 1.75 }} key={a.text} spacing={0.75}>
                <Stack
                  sx={{
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                  }}
                  direction='row'
                  spacing={1.5}
                >
                  <Typography sx={{ flex: 1, minWidth: 0 }} variant='body2'>
                    {a.text}
                  </Typography>
                  <Box
                    sx={{
                      color: 'custom.neutral.400',
                      display: 'flex',
                      pt: '2px',
                    }}
                  >
                    <Icon icon={'pen' as never} fontSize={12} />
                  </Box>
                </Stack>

                <Stack
                  sx={{ alignItems: 'center' }}
                  direction='row'
                  spacing={0.75}
                >
                  <Box sx={{ color: meta.color, display: 'flex' }}>
                    <Icon icon={meta.icon as never} fontSize={11} />
                  </Box>
                  <Typography variant='caption' color='text.secondary'>
                    {a.source}
                  </Typography>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Screen>
  );
};
C3ConfirmEdit.displayName = 'C3ConfirmEdit';

const GROUP_META = {
  confirmed: { icon: 'check-circle', color: 'custom.limeGreen.800' },
  open: { icon: 'exclamation-circle', color: 'custom.warmOrange.500' },
} as const;

// C4 — the pre-submission view, reading its open items from the same "later"
// tray C2 writes to. Confirmed groups collapse to a count rather than
// re-presenting every answer, which is the re-reading question in the flesh.
export const C4Review = () => {
  const c = AA.c4;
  return (
    <Screen
      title={c.lead}
      back
      footer={
        <Stack spacing={1.5}>
          <Button ariaLabel={c.cta} isFullWidth>
            {c.cta}
          </Button>
          <Button ariaLabel={c.help} variant='link' isFullWidth>
            {c.help}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2.5}>
        <Chip size='small' label={c.outstanding} color='warmOrange' />

        <Stack divider={<Divider />}>
          {c.groups.map((g) => {
            const meta = GROUP_META[g.state];
            return (
              <Stack
                sx={{ alignItems: 'center', py: 1.5 }}
                key={g.label}
                direction='row'
                spacing={2}
              >
                <Box sx={{ color: meta.color, display: 'flex' }}>
                  <Icon icon={meta.icon as never} fontSize={15} />
                </Box>
                <Typography sx={{ flex: 1, minWidth: 0 }} variant='body2'>
                  {g.label}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {g.count}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Screen>
  );
};
C4Review.displayName = 'C4Review';

// C5 — the "never into the void" signal. Deliberately quiet and tied to a
// reason, because the failure mode being tested is this reading as a slot
// machine rather than as progress.
export const C5NotIntoTheVoid = () => {
  const c = AA.c5;
  return (
    <Screen footer={<ChatInput placeholder='Tell me anything…' />}>
      <Stack sx={{ pt: 1 }} spacing={2}>
        <Chat turns={c.turns} />

        <Stack
          sx={{ alignItems: 'flex-start', pl: 1 }}
          direction='row'
          spacing={1.5}
        >
          <Box
            sx={{ color: 'custom.limeGreen.800', display: 'flex', pt: '2px' }}
          >
            <Icon icon={'check-circle' as never} fontSize={13} />
          </Box>
          <Typography variant='caption' color='text.secondary'>
            {c.signal}
          </Typography>
        </Stack>
      </Stack>
    </Screen>
  );
};
C5NotIntoTheVoid.displayName = 'C5NotIntoTheVoid';

// C6 — goal-seeking made visible: it stops because it has enough. The
// question-count line is doing the persuading, and is the bit most likely to
// be wrong — it either lands as a win or as the app giving up.
export const C6WhenItStops = () => {
  const c = AA.c6;
  return (
    <Screen
      footer={
        <Button ariaLabel={c.cta} isFullWidth>
          {c.cta}
        </Button>
      }
    >
      <Stack sx={{ pt: 6, alignItems: 'flex-start' }} spacing={3}>
        <Box sx={{ color: 'custom.limeGreen.800', display: 'flex' }}>
          <Icon icon={'check-circle' as never} fontSize={44} />
        </Box>

        <Stack spacing={1.5}>
          <Typography variant='h2'>{c.headline}</Typography>
          <Typography variant='body1' color='text.secondary'>
            {c.body}
          </Typography>
        </Stack>

        <Divider flexItem />

        <Typography variant='body2' color='custom.coral.700'>
          {c.detail}
        </Typography>
      </Stack>
    </Screen>
  );
};
C6WhenItStops.displayName = 'C6WhenItStops';
